<script lang="ts">
  /**
   * The shell: a live status strip, and either the logbook or one flight.
   *
   * Routing is `history.pushState` against three shapes — `/`, `/heatmap` and `/flights/{id}`
   * — which is why the server falls back to index.html for an unknown path. No router library:
   * three routes do not need one, and a deep link has to work on a reload either way.
   */
  import FlightView from './lib/FlightView.svelte';
  import Heatmap from './lib/Heatmap.svelte';
  import LiveStatus from './lib/LiveStatus.svelte';
  import Logbook from './lib/Logbook.svelte';
  import Account from './lib/Account.svelte';
  import Auth from './lib/Auth.svelte';
  import LoopingPlane from './lib/LoopingPlane.svelte';
  import { api } from './lib/api.ts';
  import type { Aircraft, AuthState } from './lib/api.ts';

  const flightIdFrom = (pathname: string): string | null => {
    const match = /^\/flights\/(.+)$/.exec(pathname);
    return match?.[1] === undefined ? null : decodeURIComponent(match[1]);
  };

  let currentId = $state(flightIdFrom(location.pathname));
  let onHeatmap = $state(location.pathname === '/heatmap');
  let aircraft = $state<Aircraft[]>([]);
  let aircraftError = $state<string | null>(null);
  let build = $state<{ version: string; build: string } | null>(null);

  /**
   * Whether the logbook may be shown at all.
   *
   * `null` while the first answer is in flight: showing the gate and then replacing it with the
   * logbook a moment later reads as a login that was not needed, and showing the logbook first
   * flashes data at somebody who may not be allowed it.
   *
   * When passkeys are not configured the daemon reports an unclaimed instance that cannot be
   * claimed, and this deliberately lets the app through — an instance on the LAN with no auth
   * configured is the behaviour that existed before any of this.
   */
  let auth = $state<AuthState | null>(null);
  let authReloads = $state(0);

  const gated = $derived(auth !== null && !auth.signedIn && (auth.claimed || auth.claimPossible));

  $effect(() => {
    void authReloads;
    const controller = new AbortController();
    api
      .authState(controller.signal)
      .then((s) => {
        auth = s;
      })
      .catch(() => {
        // An instance too old to know the endpoint, or one that cannot answer. Either way the
        // gate must not become a wall.
        auth = {
          claimed: false,
          signedIn: false,
          username: null,
          claimNeedsToken: false,
          claimPossible: false,
        };
      });
    return () => controller.abort();
  });

  /**
   * The fleet, refetched whenever the session changes.
   *
   * The dependency on `authReloads` is the point: before signing in this request is refused,
   * and without re-running it a successful login would close the gate onto an error message
   * about not being signed in. Clearing the error first matters for the same reason — a stale
   * 401 outlives the condition that caused it.
   */
  $effect(() => {
    void authReloads;
    const controller = new AbortController();
    aircraftError = null;
    api
      .aircraft(controller.signal)
      .then((list) => {
        aircraft = list;
      })
      .catch((err: unknown) => {
        if (!controller.signal.aborted) {
          aircraftError = err instanceof Error ? err.message : String(err);
        }
      });
    return () => controller.abort();
  });

  $effect(() => {
    const controller = new AbortController();
    api
      .mapConfig(controller.signal)
      .then((c) => {
        build = { version: c.version, build: c.build };
      })
      .catch(() => {
        /* A missing version line is not worth an error message. */
      });
    return () => controller.abort();
  });

  // The back button has to work, so the popstate handler is the single source of truth for
  // which view is showing; navigation only pushes and lets this react.
  $effect(() => {
    const onPop = () => {
      currentId = flightIdFrom(location.pathname);
      onHeatmap = location.pathname === '/heatmap';
    };
    addEventListener('popstate', onPop);
    return () => removeEventListener('popstate', onPop);
  });

  function open(id: string): void {
    history.pushState({}, '', `/flights/${encodeURIComponent(id)}`);
    currentId = id;
    onHeatmap = false;
    scrollTo({ top: 0 });
  }

  function back(): void {
    history.pushState({}, '', '/');
    currentId = null;
    onHeatmap = false;
  }

  function showHeatmap(): void {
    history.pushState({}, '', '/heatmap');
    currentId = null;
    onHeatmap = true;
    scrollTo({ top: 0 });
  }
</script>

<div class="shell">
  <header class="top">
    <button class="brand" onclick={back} type="button">
      <LoopingPlane width={64} seconds={6} />
      <span>flightlogd</span>
    </button>
    <div class="row">
      <nav class="tabs">
        <button class:active={!onHeatmap && currentId === null} onclick={back} type="button">
          Logbuch
        </button>
        <button class:active={onHeatmap} onclick={showHeatmap} type="button">Heatmap</button>
      </nav>
      {#if auth?.signedIn === true && auth.username !== null}
        <!-- In the header rather than a settings page: adding the *second* passkey is the one
             thing standing between one working device and none, and it should not be somewhere
             you only find after losing the first. -->
        <Account
          username={auth.username}
          onSignedOut={() => {
            aircraft = [];
            authReloads += 1;
          }}
        />
      {/if}
    </div>
    <LiveStatus {aircraft} onOpen={open} />
  </header>

  <main>
    {#if auth === null}
      <p class="state"><LoopingPlane width={132} seconds={4} label="Lädt" /></p>
    {:else if gated}
      <Auth
        {auth}
        onSignedIn={() => {
          authReloads += 1;
        }}
      />
    {:else if aircraftError !== null}
      <p class="fatal">
        Der Daemon antwortet nicht: {aircraftError}
      </p>
    {:else if currentId !== null}
      <FlightView id={currentId} {aircraft} onBack={back} />
    {:else if onHeatmap}
      <Heatmap {aircraft} />
    {:else}
      <Logbook {aircraft} onOpen={open} />
    {/if}
  </main>

  <footer>
    <span>
      Aus Netzwerkdaten des <a href="https://www.glidernet.org/" rel="noreferrer">Open Glider
      Network</a> abgeleitet. Kein zugelassener Flugdatenschreiber.
    </span>
    {#if build !== null}
      <!-- The version alone cannot answer "is this what I just pushed", which is the only
           question a version number in a homelab is ever asked. The short commit can. -->
      <span class="build" title="Version und Commit des laufenden Images">
        v{build.version}
        {#if build.build !== 'dev'}
          · <a
            href="https://github.com/iflyhere/flightlogd/commit/{build.build}"
            rel="noreferrer">{build.build.slice(0, 7)}</a
          >
        {:else}
          · dev
        {/if}
      </span>
    {/if}
  </footer>
</div>

<style>
  .shell {
    display: flex;
    flex-direction: column;
    min-height: 100%;
    max-width: 1180px;
    width: 100%;
    margin: 0 auto;
    padding: calc(var(--safe-top) + 0.6rem) 0.8rem calc(var(--safe-bottom) + 1rem);
  }

  .top {
    display: flex;
    flex-direction: column;
    gap: 0.6rem;
    margin-bottom: 1rem;
  }

  .brand {
    display: flex;
    align-items: center;
    gap: 0.15rem;
    align-self: flex-start;
    font-size: 1.05rem;
    font-weight: 700;
    letter-spacing: -0.01em;
    min-height: 36px;
    color: var(--accent);
  }
  .brand span {
    color: var(--text);
  }


  .row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-wrap: wrap;
  }

  .tabs {
    display: flex;
    gap: 0.25rem;
    background: var(--surface-2, #161a22);
    border-radius: 999px;
    padding: 0.2rem;
    align-self: flex-start;
  }
  .tabs button {
    border: 0;
    background: transparent;
    color: var(--text-dim);
    font: inherit;
    font-size: 0.85rem;
    padding: 0.35rem 0.85rem;
    border-radius: 999px;
    cursor: pointer;
  }
  .tabs button.active {
    background: var(--accent-soft);
    color: var(--accent);
    font-weight: 600;
  }

  main {
    flex: 1;
  }

  .build {
    margin-left: auto;
    font-variant-numeric: tabular-nums;
    opacity: 0.7;
    white-space: nowrap;
  }

  .state {
    padding: 3rem 1rem;
    text-align: center;
    color: var(--text-dim);
  }

  .fatal {
    padding: 3rem 1rem;
    text-align: center;
    color: var(--bad);
  }

  footer {
    margin-top: 2rem;
    padding-top: 1rem;
    border-top: 1px solid var(--line);
    font-size: 0.78rem;
    color: var(--text-faint);
    line-height: 1.5;
  }
</style>
